import { LoadingButton } from '@mui/lab';
import { Button, Divider, Grid, Stack, SvgIcon, Typography } from '@mui/material';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { forEach, get, map } from 'lodash';
import { ComponentType, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { QUESTION_LANGUAGES } from 'src/common/lang-constants';
import { DirectionsSelect } from 'src/components/directions';
import { DownloadIcon, ExcelIcon, UploadIcon } from 'src/components/icons';
import { OrganizationsSelect } from 'src/components/organizations';
import { useAuth } from 'src/hooks';
import { getQuestionList, uploadQuestions } from 'src/thunks/question-thunks';
import useStyles from './styles';

interface TestsImportFormProps {
  onCancel?: () => void;
}
export const TestsImportForm: ComponentType<TestsImportFormProps> = ({ onCancel }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { isSuperAdmin, user } = useAuth();
  const dispatch = useDispatch();
  const locale = i18n.language;

  const [lang, setLang] = useState('uz');

  const handleLangChange = (lang: string) => {
    setLang(lang);
    formik.setFieldValue('language', lang);
  };

  const [files, setFiles] = useState<File[] | null>(null);
  const onDrop = useCallback((acceptedFiles) => {
    formik.setFieldValue('file', acceptedFiles[0]);
    setFiles(acceptedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: false });

  const handleSubmit = () => {
    let formdata = new FormData();
    forEach(Object.entries(formik.values), (item: [string, any]) => {
      formdata.append(item[0], item[1]);
    });
    dispatch(uploadQuestions(formdata));
    dispatch(getQuestionList({ language: locale, embed: 'direction,direction.organization' }));
    onCancel && onCancel();
  };

  const formik = useFormik({
    initialValues: {
      language: lang,
      organization: get(user, ['data', 'organization', 'id'], ''),
      direction: '',
      file: null,
    },
    // validationSchema: {},
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Typography variant="h3" mb={3}>
        {t('download.template.title', 'Скачать шаблон для заполнения')}
      </Typography>
      <Button
        href="/assets/attestat_questions_template.xlsx"
        download
        variant="contained"
        color="secondary"
        endIcon={<DownloadIcon />}
        size="small"
      >
        {t('download.template', 'Скачать шаблон')}
      </Button>
      <Typography variant="body2" mt={2}>
        {t(
          'download.template.hint',
          'Скачайте шаблон файла, заполните его и загрузите в поле ниже.'
        )}
      </Typography>
      <Divider sx={{ my: 3 }} />
      <Typography variant="h3" mb={3}>
        {t('upload.testsFile', 'Загрузить файл с тестами')}
      </Typography>
      <Stack spacing={3} className={classes.langButtons} direction="row" mb={3}>
        {map(QUESTION_LANGUAGES, (item) => (
          <Button
            onClick={() => handleLangChange(item.code)}
            className={clsx(classes.langButton, { [classes.activeLangButton]: item.code === lang })}
            key={item.code}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      <Grid container columnSpacing={3}>
        {isSuperAdmin && (
          <Grid item xs={6}>
            <OrganizationsSelect
              {...formik.getFieldProps('organization')}
              // onChange={(e: any) => handleFieldChange('organization', e)}
              // error={hasError('organization')}
              // helperText={getErrorMessage('organization')}
              label={t('organizations.name', 'Название организации')}
              placeholder={t('organizations.name.placeholder', 'Введите название организации')}
              // disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={isSuperAdmin ? 6 : 12}>
          <DirectionsSelect
            organization={formik.values.organization}
            {...formik.getFieldProps('direction')}
            // onChange={(e: any) => handleFieldChange('direction', e)}
            label={t('direction', 'Направление')}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Stack
            className={clsx(classes.fileuploaderDropzone, {
              [classes.activeUploader]: isDragActive,
            })}
            alignItems="center"
            justifyContent="center"
            {...getRootProps()}
            mt={3}
          >
            <input {...getInputProps()} />
            <UploadIcon />
            <Typography variant="body3" mt={1}>
              {t('upload.file.placeholder')}
            </Typography>
          </Stack>

          <div>
            {map(files, (file, index) => (
              <Stack direction="row" alignItems="center" mt={2} key={index}>
                {/* <Typography color="primary"> */}
                <SvgIcon component={ExcelIcon} sx={{ fontSize: 40 }} color="primary" />
                {/* </Typography> */}
                <Typography variant="body2" ml={1}>
                  {file.name}
                </Typography>
              </Stack>
            ))}
          </div>
        </Grid>
      </Grid>

      <Stack spacing={2} justifyContent="space-between" direction="row" mt={4}>
        {onCancel && (
          <Button
            onClick={onCancel}
            variant="outlined"
            size="large"
            // disabled={isLoading}
          >
            {t('action.cancel', 'Отмена')}
          </Button>
        )}
        <LoadingButton
          // loading={isLoading || isFetching}
          type="submit"
          variant="contained"
          color="secondary"
          size="large"
        >
          {t('action.save', 'Сохранить')}
        </LoadingButton>
      </Stack>
    </form>
  );
};
